import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, To, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCheckCircle,
    faTimesCircle,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import Dropdown, {
    Item as DropdownItem,
} from '../../common/form/inputs/DropDown'
import personApi, {
    PersonNetworkImportStatusSummaryDto,
} from '../../../api/person/PersonApi'
import networkApi from '../../../api/network/NetworkApi'
import {
    CsPersonSearchResultDto,
    CsPersonWorkHistoryFilter,
} from '../../../api/person/models/searchCs/SearchPersonsCsDtos'
import { formatPersonName } from '../../../utils/personUtils'
import ProfilePic from '../../common/person/ProfilePic'
import Button from '../../common/form/inputs/Button'
import LinkedInLink from '../../../components/LinkedInLink'
import LoadingSpinner from '../../common/loading/LoadingSpinner'
import {
    PersonNetworkImportLogStatusBadge,
    PersonNetworkImportStatusBadge,
} from '../../../components/dataManagement/PersonNetworkImportBadges'
import { useSafeUserData } from '../../../stateStores/User'

const DEFAULT_DEBOUNCE_MS = 300
const NETWORK_IMPORT_TRIGGER_STICKY_MS = 30_000

const PERSON_SEARCH_QUERY_PARAM = 'personSearch'
const PERSON_SEARCH_MODE_PARAM = 'personSearchMode'
const PERSON_SEARCH_WORK_HISTORY_FILTER_PARAM = 'workHistoryFilter'

type PersonSearchMode =
    | 'id'
    | 'full_name'
    | 'first_last_name'
    | 'last_name'
    | 'li_url'

type SearchOption = {
    value: PersonSearchMode
    label: string
}

const DEFAULT_SEARCH_OPTIONS: readonly SearchOption[] = [
    { value: 'id', label: 'Person ID' },
    { value: 'full_name', label: 'Full Name' },
    { value: 'first_last_name', label: 'First & Last Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'li_url', label: 'LinkedIn URL' },
] as const

const DEFAULT_SEARCH_MODE: PersonSearchMode = 'full_name'

const DEFAULT_WORK_HISTORY_FILTER: CsPersonWorkHistoryFilter = 'all'

const WORK_HISTORY_FILTER_ITEMS: DropdownItem[] = [
    { id: 'all', content: 'All' },
    {
        id: 'exclude_no_work_history',
        content: (
            <span className="inline-flex items-center gap-2">
                <span>Ignore</span>
                <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="text-red-600"
                    aria-hidden="true"
                />
                <span>no work history items</span>
            </span>
        ),
    },
    {
        id: 'exclude_assumed_start_dates',
        content: (
            <span className="inline-flex items-center gap-2">
                <span>Ignore</span>
                <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="text-warning-700"
                    aria-hidden="true"
                />
                <span>assumed start dates</span>
            </span>
        ),
    },
    {
        id: 'exclude_no_work_history_or_assumed_start_dates',
        content: (
            <span className="inline-flex items-center gap-2">
                <span>Ignore</span>
                <span className="inline-flex items-center gap-1">
                    <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className="text-red-600"
                        aria-hidden="true"
                    />
                    <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className="text-warning-700"
                        aria-hidden="true"
                    />
                </span>
                <span>both no work history and assumed start dates</span>
            </span>
        ),
    },
]

const parsePersonSearchMode = (
    value: string | null,
    fallback: PersonSearchMode,
): PersonSearchMode => {
    if (value === 'id') return 'id'
    if (value === 'first_last_name') return 'first_last_name'
    if (value === 'last_name') return 'last_name'
    if (value === 'li_url') return 'li_url'
    if (value === 'full_name') return 'full_name'
    return fallback
}

const parseWorkHistoryFilter = (
    value: string | null,
    fallback: CsPersonWorkHistoryFilter,
): CsPersonWorkHistoryFilter => {
    if (value === 'all') return 'all'
    if (value === 'exclude_no_work_history') return 'exclude_no_work_history'
    if (value === 'exclude_assumed_start_dates')
        return 'exclude_assumed_start_dates'
    if (value === 'exclude_no_work_history_or_assumed_start_dates') {
        return 'exclude_no_work_history_or_assumed_start_dates'
    }
    return fallback
}

const parseWorkHistoryFilterItem = (value: string): CsPersonWorkHistoryFilter =>
    parseWorkHistoryFilter(value, DEFAULT_WORK_HISTORY_FILTER)

type Props = {
    allowEmptyQuery?: boolean
    minQueryLength?: number
    searchDebounceMs?: number
    enableUrlSync?: boolean
    allowImport?: boolean
    allowNetworkImport?: boolean
    allowGenerateNetwork?: boolean
    onSelectPerson?: (result: CsPersonSearchResultDto) => Promise<void> | void
    canSelectResult?: (result: CsPersonSearchResultDto) => boolean
    selectButtonLabel?: string
    selectColumnLabel?: string
    searchPlaceholder?: string
    showSearchModeSelector?: boolean
    searchModeOptions?: readonly SearchOption[]
    defaultSearchMode?: PersonSearchMode
    tableWrapperClassName?: string
    enableWorkHistoryLinks?: boolean
    buildWorkHistoryLink?: (personId: string) => To
    enableWorkHistoryFilters?: boolean
    emptyStateMessage?: string
}

export default function CsPersonSearch({
    allowEmptyQuery = true,
    minQueryLength = 0,
    searchDebounceMs = DEFAULT_DEBOUNCE_MS,
    enableUrlSync = false,
    allowImport = true,
    allowNetworkImport = true,
    onSelectPerson,
    canSelectResult,
    selectButtonLabel = 'Select',
    selectColumnLabel = 'Select',
    searchPlaceholder = 'Search by person ID or name',
    showSearchModeSelector = true,
    searchModeOptions = DEFAULT_SEARCH_OPTIONS,
    defaultSearchMode = DEFAULT_SEARCH_MODE,
    tableWrapperClassName,
    enableWorkHistoryLinks = true,
    buildWorkHistoryLink,
    enableWorkHistoryFilters = false,
    emptyStateMessage = 'No matches',
}: Props): React.JSX.Element {
    const { user } = useSafeUserData()
    const isProductAdmin = user.isProductAdmin
    const [searchParams, setSearchParams] = useSearchParams()

    const resolvedDefaultSearchMode = useMemo(() => {
        if (
            searchModeOptions.some(
                (option) => option.value === defaultSearchMode,
            )
        ) {
            return defaultSearchMode
        }
        return searchModeOptions[0]?.value ?? DEFAULT_SEARCH_MODE
    }, [defaultSearchMode, searchModeOptions])

    const [query, setQuery] = useState<string>(() => {
        if (!enableUrlSync) return ''
        return searchParams.get(PERSON_SEARCH_QUERY_PARAM) || ''
    })

    const [searchMode, setSearchMode] = useState<PersonSearchMode>(() => {
        if (!enableUrlSync) return resolvedDefaultSearchMode
        return parsePersonSearchMode(
            searchParams.get(PERSON_SEARCH_MODE_PARAM),
            resolvedDefaultSearchMode,
        )
    })

    const [workHistoryFilter, setWorkHistoryFilter] =
        useState<CsPersonWorkHistoryFilter>(() => {
            if (!enableUrlSync || !enableWorkHistoryFilters) {
                return DEFAULT_WORK_HISTORY_FILTER
            }
            return parseWorkHistoryFilter(
                searchParams.get(PERSON_SEARCH_WORK_HISTORY_FILTER_PARAM),
                DEFAULT_WORK_HISTORY_FILTER,
            )
        })

    const [results, setResults] = useState<CsPersonSearchResultDto[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | undefined>()
    const [selectionError, setSelectionError] = useState<string | undefined>()
    const [pageNumber, setPageNumber] = useState(1)
    const [perPage, setPerPage] = useState(0)
    const [hasMore, setHasMore] = useState(false)
    const [selectingId, setSelectingId] = useState<string | null>(null)

    const [networkImportStatuses, setNetworkImportStatuses] = useState<
        Record<string, PersonNetworkImportStatusSummaryDto>
    >({})
    const [importing, setImporting] = useState<Record<string, boolean>>({})
    const [networkImporting, setNetworkImporting] = useState<
        Record<string, boolean>
    >({})
    const [refreshToken, setRefreshToken] = useState(0)
    const networkImportTriggerTimes = useRef<Record<string, number>>({})

    const searchResultsPerPage = 50
    const listResultsPerPage = 100

    const renderInlineSpinner = (size = 16) => (
        <LoadingSpinner
            spinnerSize={size}
            captions={[]}
            className="flex-row items-center justify-center h-auto w-auto"
        />
    )

    const trimmedQuery = query.trim()
    const meetsQueryThreshold = trimmedQuery.length >= minQueryLength
    const canSearch = allowEmptyQuery || meetsQueryThreshold

    useEffect(() => {
        if (!enableUrlSync) return
        const urlQuery = searchParams.get(PERSON_SEARCH_QUERY_PARAM) || ''
        setQuery((prev) => (prev !== urlQuery ? urlQuery : prev))
        const urlMode = parsePersonSearchMode(
            searchParams.get(PERSON_SEARCH_MODE_PARAM),
            resolvedDefaultSearchMode,
        )
        setSearchMode((prev) => (prev !== urlMode ? urlMode : prev))
        if (enableWorkHistoryFilters) {
            const urlFilter = parseWorkHistoryFilter(
                searchParams.get(PERSON_SEARCH_WORK_HISTORY_FILTER_PARAM),
                DEFAULT_WORK_HISTORY_FILTER,
            )
            setWorkHistoryFilter((prev) =>
                prev !== urlFilter ? urlFilter : prev,
            )
        }
    }, [
        enableUrlSync,
        enableWorkHistoryFilters,
        searchParams,
        resolvedDefaultSearchMode,
    ])

    useEffect(() => {
        if (!enableUrlSync) return
        const current = searchParams.toString()
        const next = new URLSearchParams(searchParams)
        if (query) next.set(PERSON_SEARCH_QUERY_PARAM, query)
        else next.delete(PERSON_SEARCH_QUERY_PARAM)
        if (searchMode !== resolvedDefaultSearchMode) {
            next.set(PERSON_SEARCH_MODE_PARAM, searchMode)
        } else {
            next.delete(PERSON_SEARCH_MODE_PARAM)
        }
        if (enableWorkHistoryFilters) {
            if (workHistoryFilter !== DEFAULT_WORK_HISTORY_FILTER) {
                next.set(
                    PERSON_SEARCH_WORK_HISTORY_FILTER_PARAM,
                    workHistoryFilter,
                )
            } else {
                next.delete(PERSON_SEARCH_WORK_HISTORY_FILTER_PARAM)
            }
        } else {
            next.delete(PERSON_SEARCH_WORK_HISTORY_FILTER_PARAM)
        }
        if (next.toString() !== current) {
            setSearchParams(next, { replace: true })
        }
    }, [
        enableUrlSync,
        enableWorkHistoryFilters,
        query,
        searchMode,
        workHistoryFilter,
        searchParams,
        setSearchParams,
        resolvedDefaultSearchMode,
    ])

    useEffect(() => {
        let ignore = false

        if (!canSearch) {
            setResults([])
            setHasMore(false)
            setPerPage(0)
            setLoading(false)
            setError(undefined)
            return
        }

        const handle = window.setTimeout(async () => {
            try {
                setLoading(true)
                const trimmed = trimmedQuery
                const res = trimmed
                    ? searchMode === 'id'
                        ? await personApi.searchPersonsCsById(
                              trimmed,
                              pageNumber,
                              searchResultsPerPage,
                              { workHistoryFilter },
                          )
                        : await personApi.searchPersonsCs(trimmed, {
                              pageNumber,
                              perPage: searchResultsPerPage,
                              searchMode,
                              workHistoryFilter,
                          })
                    : await personApi.listPersonsCs(
                          pageNumber,
                          listResultsPerPage,
                          { workHistoryFilter },
                      )
                if (ignore) return
                setResults(res.results)
                setHasMore(res.hasMore)
                setPerPage(res.perPage)
                setError(undefined)
            } catch (e: any) {
                if (!ignore) setError(e?.message || 'Search failed')
            } finally {
                if (!ignore) setLoading(false)
            }
        }, searchDebounceMs)

        return () => {
            window.clearTimeout(handle)
            ignore = true
        }
    }, [
        canSearch,
        listResultsPerPage,
        pageNumber,
        refreshToken,
        searchDebounceMs,
        searchMode,
        searchResultsPerPage,
        trimmedQuery,
        workHistoryFilter,
    ])

    useEffect(() => {
        setPageNumber(1)
    }, [trimmedQuery, searchMode, workHistoryFilter])

    const workHistoryFilterDropdownItem = useMemo(() => {
        return (
            WORK_HISTORY_FILTER_ITEMS.find(
                (item) => item.id === workHistoryFilter,
            ) || WORK_HISTORY_FILTER_ITEMS[0]
        )
    }, [workHistoryFilter])

    const personIdsForStatus = useMemo(() => {
        const ids = new Set<string>()
        for (const result of results) {
            const resolvedId = (result.existingPersonId ?? result.id)?.trim()
            if (resolvedId) {
                ids.add(resolvedId)
            }
        }
        return Array.from(ids)
    }, [results])

    const idsNeedingNetworkPoll = useMemo(
        () =>
            personIdsForStatus.filter((id) => {
                const summary = networkImportStatuses[id]
                return summary?.status === 'IN_PROGRESS' || networkImporting[id]
            }),
        [personIdsForStatus, networkImportStatuses, networkImporting],
    )

    useEffect(() => {
        if (!canSearch) return
        if (personIdsForStatus.length === 0) {
            setNetworkImportStatuses({})
            return
        }
        let ignore = false
        ;(async () => {
            try {
                const importStatuses =
                    await personApi.getNetworkImportStatuses(personIdsForStatus)
                if (!ignore) {
                    setNetworkImportStatuses(importStatuses)
                }
            } catch {
                if (!ignore) {
                    setNetworkImportStatuses({})
                }
            }
        })()
        return () => {
            ignore = true
        }
    }, [canSearch, personIdsForStatus])

    useEffect(() => {
        if (idsNeedingNetworkPoll.length === 0) return

        let cancelled = false
        let timeoutId: number | undefined
        let inFlight = false

        const poll = async () => {
            if (cancelled || inFlight) return
            inFlight = true
            try {
                const latestImportStatuses =
                    await personApi.getNetworkImportStatuses(
                        idsNeedingNetworkPoll,
                    )
                if (cancelled) return
                const now = Date.now()
                const completedIds: string[] = []

                setNetworkImportStatuses((prev) => {
                    let changed = false
                    const next: Record<
                        string,
                        PersonNetworkImportStatusSummaryDto
                    > = { ...prev }
                    for (const [id, summary] of Object.entries(
                        latestImportStatuses,
                    )) {
                        const prevSummary = prev[id]
                        const prevStatus = prevSummary?.status
                        const nextStatus = summary.status
                        if (
                            !summary.hasStatusRow &&
                            prevStatus === 'IN_PROGRESS'
                        ) {
                            const triggeredAt =
                                networkImportTriggerTimes.current[id]
                            if (
                                triggeredAt &&
                                now - triggeredAt <
                                    NETWORK_IMPORT_TRIGGER_STICKY_MS
                            ) {
                                continue
                            }
                            delete networkImportTriggerTimes.current[id]
                        }
                        if (
                            prevStatus === 'IN_PROGRESS' &&
                            nextStatus !== 'IN_PROGRESS'
                        ) {
                            completedIds.push(id)
                        }

                        const shouldUpdate =
                            !prevSummary ||
                            prevSummary.status !== summary.status ||
                            prevSummary.hasStatusRow !== summary.hasStatusRow ||
                            prevSummary.latestLogStatus !==
                                summary.latestLogStatus ||
                            prevSummary.errMsg !== summary.errMsg

                        if (shouldUpdate) {
                            changed = true
                            next[id] = summary
                        }

                        if (nextStatus !== 'IN_PROGRESS') {
                            delete networkImportTriggerTimes.current[id]
                        }
                    }
                    return changed ? next : prev
                })

                if (completedIds.length) {
                    setRefreshToken((prev) => prev + 1)
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to poll network import status', err)
                }
            } finally {
                inFlight = false
                if (!cancelled) {
                    timeoutId = window.setTimeout(poll, 2000)
                }
            }
        }

        poll()

        return () => {
            cancelled = true
            if (timeoutId !== undefined) window.clearTimeout(timeoutId)
        }
    }, [idsNeedingNetworkPoll])

    const onPrev = () => {
        setPageNumber((p) => Math.max(1, p - 1))
    }
    const onNext = () => {
        setPageNumber((p) => p + 1)
    }

    const handleImport = async (personId: string) => {
        if (!allowImport) return
        setImporting((prev) => ({ ...prev, [personId]: true }))
        try {
            const res = await personApi.importPersonFromCs({
                personId: personId,
            })
            setResults((prev) =>
                prev.map((r) =>
                    r.id === personId
                        ? { ...r, existingPersonId: res.personId }
                        : r,
                ),
            )
            setError(undefined)
        } catch (e: any) {
            setError(e?.message || 'Import failed')
        } finally {
            setImporting((prev) => ({ ...prev, [personId]: false }))
        }
    }

    const handleImportNetwork = async (personId: string) => {
        if (!allowNetworkImport) return
        setNetworkImporting((prev) => ({
            ...prev,
            [personId]: true,
        }))
        try {
            await networkApi.queueNetworkImport(
                {
                    personIds: [personId],
                },
                isProductAdmin,
            )
            networkImportTriggerTimes.current[personId] = Date.now()
            setNetworkImportStatuses((prev) => ({
                ...prev,
                [personId]: {
                    status: 'IN_PROGRESS',
                    hasStatusRow: true,
                    latestLogStatus: prev[personId]?.latestLogStatus,
                },
            }))
            setError(undefined)
        } catch (e: any) {
            delete networkImportTriggerTimes.current[personId]
            setError(e?.message || 'Failed to queue network import')
        } finally {
            setNetworkImporting((prev) => ({
                ...prev,
                [personId]: false,
            }))
        }
    }

    const handleSelect = async (result: CsPersonSearchResultDto) => {
        if (!onSelectPerson) return
        setSelectionError(undefined)
        setSelectingId(result.id)
        try {
            await onSelectPerson(result)
        } catch (e: any) {
            setSelectionError(e?.message || 'Failed to select person')
        } finally {
            setSelectingId(null)
        }
    }

    const buildTabSearch = (
        tabSlug: string,
        extraParams: Record<string, string | undefined>,
    ): To => {
        const params = new URLSearchParams()
        params.set('tab', tabSlug)
        Object.entries(extraParams).forEach(([key, value]) => {
            if (value) params.set(key, value)
        })
        const search = params.toString()
        return { pathname: '', search: search ? `?${search}` : '' }
    }

    const resolvedWorkHistoryLink = (personId: string): To | undefined => {
        if (!enableWorkHistoryLinks) return undefined
        if (buildWorkHistoryLink) return buildWorkHistoryLink(personId)
        return buildTabSearch('work-history-cold-storage', {
            workHistorySearch: personId,
        })
    }

    const selectEnabledFor = (person: CsPersonSearchResultDto) => {
        if (!onSelectPerson) return false
        if (selectingId) return false
        return canSelectResult ? canSelectResult(person) : true
    }

    const shouldShowTable = allowEmptyQuery || canSearch

    return (
        <div className="flex flex-col gap-3">
            <input
                className="form-input"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={Boolean(selectingId)}
            />
            {showSearchModeSelector && searchModeOptions.length > 1 && (
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
                    <span className="font-semibold">Search by:</span>
                    {searchModeOptions.map((option) => (
                        <label
                            key={option.value}
                            className="inline-flex items-center gap-2"
                        >
                            <input
                                type="radio"
                                className="form-radio"
                                name="person-search-mode"
                                value={option.value}
                                checked={searchMode === option.value}
                                onChange={() => setSearchMode(option.value)}
                                disabled={Boolean(selectingId)}
                            />
                            {option.label}
                        </label>
                    ))}
                </div>
            )}
            {enableWorkHistoryFilters && (
                <div className="w-full max-w-xs">
                    <Dropdown
                        id="work-history-filter"
                        name="Work History Filter"
                        placeholder="Select work history filter"
                        items={WORK_HISTORY_FILTER_ITEMS}
                        selected={workHistoryFilterDropdownItem}
                        onChange={(item) =>
                            setWorkHistoryFilter(
                                parseWorkHistoryFilterItem(item.id),
                            )
                        }
                    />
                </div>
            )}
            {loading && (
                <div className="py-4">
                    <LoadingSpinner
                        spinnerSize={48}
                        captions={[
                            {
                                caption: trimmedQuery
                                    ? 'Searching people…'
                                    : 'Loading people…',
                            },
                        ]}
                        className="h-auto w-full"
                    />
                </div>
            )}
            {error && <div className="text-red-600">{error}</div>}
            {selectionError && (
                <div className="text-red-600">{selectionError}</div>
            )}

            {shouldShowTable && (
                <>
                    <div className={tableWrapperClassName ?? 'overflow-x-auto'}>
                        <table className="main-table w-full">
                            <thead>
                                <tr>
                                    <th className="w-12">Pic</th>
                                    <th>Name</th>
                                    <th>Headline</th>
                                    <th>Id</th>
                                    <th>Data Provider ID</th>
                                    <th>LinkedIn</th>
                                    <th>Work History Entries</th>
                                    <th>Imported</th>
                                    <th>Network Import Status</th>
                                    {onSelectPerson && (
                                        <th className="text-right">
                                            {selectColumnLabel}
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r) => {
                                    const existingPersonId = r.existingPersonId
                                    const resolvedPersonId = (
                                        existingPersonId ?? r.id
                                    )?.trim()
                                    const imported = Boolean(existingPersonId)
                                    const statusSummary = resolvedPersonId
                                        ? networkImportStatuses[
                                              resolvedPersonId
                                          ]
                                        : undefined
                                    const networkImportBusy =
                                        (resolvedPersonId
                                            ? Boolean(
                                                  networkImporting[
                                                      resolvedPersonId
                                                  ],
                                              )
                                            : false) ||
                                        statusSummary?.status === 'IN_PROGRESS'
                                    const canTriggerNetworkImport = Boolean(
                                        allowNetworkImport &&
                                        resolvedPersonId &&
                                        !networkImportBusy,
                                    )
                                    const showNoStatusRowNote =
                                        statusSummary?.hasStatusRow === false
                                    const latestLogStatus =
                                        statusSummary?.latestLogStatus
                                    const showStatusSpinner = Boolean(
                                        resolvedPersonId &&
                                        !statusSummary &&
                                        personIdsForStatus.includes(
                                            resolvedPersonId,
                                        ),
                                    )
                                    const statusLabel = statusSummary?.status
                                    const headline = r.headline?.trim()
                                    const selectEnabled = selectEnabledFor(r)
                                    const assumedStartDateCount =
                                        r.assumedStartDateCount ?? 0
                                    return (
                                        <tr key={r.id}>
                                            <td className="w-12">
                                                <ProfilePic
                                                    src={
                                                        r.linkedInProfilePicUrl
                                                    }
                                                    size={32}
                                                    alt={formatPersonName(r)}
                                                />
                                            </td>
                                            <td className="whitespace-nowrap">
                                                {formatPersonName(r)}
                                            </td>
                                            <td className="text-sm text-gray-700">
                                                {headline ? (
                                                    <span title={r.headline}>
                                                        {headline}
                                                    </span>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td className="text-xs text-gray-700">
                                                {r.id}
                                            </td>
                                            <td className="text-xs text-gray-700">
                                                {r.dataProviderId ?? '-'}
                                            </td>
                                            <td>
                                                {r.linkedInUrl ? (
                                                    <LinkedInLink
                                                        url={r.linkedInUrl}
                                                    />
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td className="text-right">
                                                <span className="inline-flex items-center gap-2">
                                                    {r.workHistoryCount > 0
                                                        ? (() => {
                                                              const link =
                                                                  resolvedWorkHistoryLink(
                                                                      r.id,
                                                                  )
                                                              const label =
                                                                  r.workHistoryCount.toLocaleString()
                                                              return link ? (
                                                                  <Link
                                                                      to={link}
                                                                      className="text-blue-600 hover:underline"
                                                                  >
                                                                      {label}
                                                                  </Link>
                                                              ) : (
                                                                  <span>
                                                                      {label}
                                                                  </span>
                                                              )
                                                          })()
                                                        : r.workHistoryCount.toLocaleString()}
                                                    {r.workHistoryCount ===
                                                        0 && (
                                                        <span
                                                            className="text-red-600"
                                                            title="This person has no work history items in our database"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    faTriangleExclamation
                                                                }
                                                            />
                                                        </span>
                                                    )}
                                                    {r.workHistoryCount > 0 &&
                                                        assumedStartDateCount >
                                                            0 && (
                                                            <span
                                                                className="text-warning-700"
                                                                title={`This person has ${assumedStartDateCount.toLocaleString()} work history items that have an assumed start date`}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faTriangleExclamation
                                                                    }
                                                                />
                                                            </span>
                                                        )}
                                                </span>
                                            </td>
                                            <td>
                                                {imported ? (
                                                    <FontAwesomeIcon
                                                        icon={faCheckCircle}
                                                        className="text-green-600"
                                                        title="Imported"
                                                    />
                                                ) : allowImport ? (
                                                    networkImportBusy ? (
                                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                                            {renderInlineSpinner(
                                                                14,
                                                            )}
                                                            <span>
                                                                Network import
                                                                running…
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <Button
                                                            type="button"
                                                            style="primary"
                                                            onClick={() =>
                                                                handleImport(
                                                                    r.id,
                                                                )
                                                            }
                                                            enabled={
                                                                !importing[
                                                                    r.id
                                                                ] &&
                                                                !selectingId
                                                            }
                                                        >
                                                            {importing[r.id] ? (
                                                                <span className="inline-flex items-center gap-2">
                                                                    {renderInlineSpinner()}
                                                                    Importing…
                                                                </span>
                                                            ) : (
                                                                'Import'
                                                            )}
                                                        </Button>
                                                    )
                                                ) : (
                                                    <FontAwesomeIcon
                                                        icon={faTimesCircle}
                                                        className="text-red-600"
                                                        title="Not imported"
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                {resolvedPersonId ? (
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2">
                                                            {showStatusSpinner ? (
                                                                renderInlineSpinner()
                                                            ) : statusLabel ? (
                                                                <PersonNetworkImportStatusBadge
                                                                    status={
                                                                        statusLabel
                                                                    }
                                                                />
                                                            ) : (
                                                                <span>-</span>
                                                            )}
                                                        </div>
                                                        {showNoStatusRowNote && (
                                                            <span className="text-xs text-gray-500">
                                                                (no status row)
                                                            </span>
                                                        )}
                                                        <div className="flex items-center gap-1 text-xs text-gray-500">
                                                            {latestLogStatus && (
                                                                <>
                                                                    <span>
                                                                        Latest
                                                                        log:
                                                                    </span>

                                                                    <PersonNetworkImportLogStatusBadge
                                                                        status={
                                                                            latestLogStatus
                                                                        }
                                                                    />
                                                                </>
                                                            )}
                                                        </div>
                                                        {allowNetworkImport &&
                                                        canTriggerNetworkImport ? (
                                                            <Button
                                                                type="button"
                                                                style="secondary"
                                                                onClick={() =>
                                                                    handleImportNetwork(
                                                                        resolvedPersonId,
                                                                    )
                                                                }
                                                                enabled={Boolean(
                                                                    !networkImportBusy &&
                                                                    !selectingId,
                                                                )}
                                                            >
                                                                {networkImportBusy ? (
                                                                    <span className="inline-flex items-center gap-2">
                                                                        {renderInlineSpinner()}
                                                                        Importing…
                                                                    </span>
                                                                ) : (
                                                                    'Import Network'
                                                                )}
                                                            </Button>
                                                        ) : null}
                                                        {networkImportBusy && (
                                                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                                                {renderInlineSpinner(
                                                                    14,
                                                                )}
                                                                <span>
                                                                    Import
                                                                    running…
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            {onSelectPerson && (
                                                <td className="text-right">
                                                    <Button
                                                        type="button"
                                                        style="primary"
                                                        onClick={() =>
                                                            handleSelect(r)
                                                        }
                                                        enabled={selectEnabled}
                                                    >
                                                        {selectingId ===
                                                        r.id ? (
                                                            <span className="inline-flex items-center gap-2">
                                                                {renderInlineSpinner()}
                                                                Selecting…
                                                            </span>
                                                        ) : (
                                                            selectButtonLabel
                                                        )}
                                                    </Button>
                                                </td>
                                            )}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {!loading && results.length === 0 && (
                        <div className="text-sm text-gray-600">
                            {emptyStateMessage}
                        </div>
                    )}

                    <div className="flex justify-between items-center mt-2">
                        <Button
                            type="button"
                            style="secondary"
                            onClick={onPrev}
                            enabled={pageNumber > 1 && !loading}
                        >
                            Previous
                        </Button>
                        <div className="text-sm text-gray-600">
                            Page {pageNumber}
                            {perPage ? ` · Showing ${results.length}` : ''}
                            {perPage ? ` · Per page ${perPage}` : ''}
                        </div>
                        <Button
                            type="button"
                            style="secondary"
                            onClick={onNext}
                            enabled={!loading && hasMore}
                        >
                            Next
                        </Button>
                    </div>
                </>
            )}
        </div>
    )
}

export type { PersonSearchMode, SearchOption }
